<script setup lang="ts">
import IndividualLayout from '@/modules/investing/components/individuals/individual-layout.vue'
import { useRoute } from 'vue-router'
import { computed, onMounted, ref } from 'vue'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'
import { VButton, VDropdownImproved, VIcon, VSection, VTable } from '@/modules/shared/components'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { get } from 'lodash'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import VSelectInvestor from '@/modules/investing/components/VSelectInvestor.vue'
import { useFetch as useFetchV4 } from '@/modules/core/composables/useFetch'
import { useActionsMenu } from '@/modules/shared/utils/actions-menu'

const route = useRoute()
const individualStore = useIndividualStore()

// Tax Documents
const documents = computed(() => individualStore.listTaxDocuments)
const filteredDocuments = computed(() =>
  documents.value.filter((document) => selectedYears.value.includes(document.tax_year)),
)

// Tax Year Options
const selectedYears = ref([])
const taxYearOptions = computed(() => {
  const years = [{ label: default_year, value: default_year }]
  const yearsAdded = [default_year]
  documents.value.forEach((document) => {
    if (!yearsAdded.includes(document.tax_year)) {
      years.push({ label: document.tax_year, value: document.tax_year })
      yearsAdded.push(document.tax_year)
    }
  })

  return years
})
const selectedYearsString = computed(() =>
  selectedYears.value.length > 0 ? selectedYears.value.join(', ') : '(not selected)',
)

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const { individual_id, slug } = route.params as { individual_id: string; slug: string }

const fetch = async (shareholder_cids = null) => {
  showSkeleton()
  await individualStore.fetchIndividual(individual_id, { slug, shareholder_cids })
  await individualStore.fetchTaxDocuments(individual_id, {
    slug: slug,
    shareholder_cids: shareholder_cids,
  })
  hideSkeleton()
}
const deleteDocument = async (document) => {
  const confirm = window.confirm('Are you sure you want to delete the document?')
  if (!confirm) return

  actionsMenu.setLoading('delete', true)
  await useFetchV4(`/v4/${slug}/tax/documents/${document.uuid}`).delete()
  actionsMenu.setLoading('delete', false)

  await fetch(selectedCids.value)
}

///////////////////////////////////////////////////////////////////////////////
// Authorization
///////////////////////////////////////////////////////////////////////////////

const authStore = useAuthStore()
const can_access_entities = computed(
  () =>
    route.params.individual_id === authStore.current_user.investor_id?.toString() || authStore.is_site_or_group_admin,
)
const isSiteAdmin = computed(() => authStore.is_site_admin)

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()
const actionsMenu = useActionsMenu(['delete'])
const default_year = new Date().getFullYear() - 1

const selectedCids = computed(() =>
  individualStore.selectedShareholderKeys.map((keys) => JSON.parse(keys).reverse().join(':')),
)

const onFilter = async () => {
  await fetch(selectedCids.value)
}

onMounted(async () => {
  await fetch()
  selectedYears.value = [default_year]
})
</script>

<template>
  <IndividualLayout selectedTab="taxes">
    <VSection>
      <div class="z-50 flex items-center justify-between">
        <div class="mb-6 flex items-center space-x-2">
          <VDropdownImproved v-model="selectedYears" aligned="left" :options="taxYearOptions">
            <VButton size="md">
              <div class="flex items-center space-x-2">
                <div>Filter by year {{ selectedYearsString }}</div>
                <VIcon name="ChevronSelectorVertical" />
              </div>
            </VButton>
          </VDropdownImproved>
          <VSelectInvestor
            v-model="individualStore.selectedShareholderKeys"
            :investors="individualStore.listShareholders"
            :onClose="onFilter"
          />
        </div>
      </div>

      <VTable
        :columns="[
          {
            key: 'actions',
            name: '',
            type: 'actions',
            is_visible: true,
          },
          {
            key: 'fileable.name',
            name: 'Entity',
            type: 'string',
            is_visible: true,
            sorted: true,
          },
          {
            key: 'shareholder.name',
            name: 'Shareholder',
            type: 'string',
            is_visible: false,
          },
          {
            key: 'attachment_original_file_name',
            name: 'File Name',
            type: 'string',
            is_visible: true,
          },
          {
            key: 'updated_at',
            name: 'Updated at',
            type: 'date',
            is_visible: true,
          },
          {
            key: 'tax_year',
            name: 'Year',
            type: 'string',
            is_visible: true,
          },
        ]"
        :items="filteredDocuments"
        :name="`${route.params.slug}-documents`"
        :skeleton="skeleton"
        :slots="['fileable.name', 'attachment_download_file_name', 'shareholder.name', 'actions']"
      >
        <template #fileable.name="{ item }">
          <div class="mb-1 flex w-72 items-center justify-between">
            <div>
              <RouterLink
                class="hyperlink"
                :to="{
                  name: 'investing.entity-overview',
                  params: { entity_id: get(item, 'fileable.id'), entity_type: get(item, 'fileable.type') },
                }"
                v-if="can_access_entities"
              >
                {{ get(item, 'fileable.name') }}
              </RouterLink>
              <template v-else>
                {{ get(item, 'fileable.name') }}
              </template>
            </div>
          </div>
        </template>
        <template #shareholder.name="{ item }">
          <p class="max-w-lg overflow-hidden text-ellipsis" :title="item.name">
            {{ get(item, 'shareholder.name') }}
          </p>
        </template>
        <template #attachment_download_file_name="{ item }">
          <p class="max-w-xl overflow-hidden text-ellipsis" :title="item.attachment_download_file_name">
            {{ item.attachment_download_file_name }}
          </p>
        </template>
        <template #actions="{ item }">
          <div class="flex space-x-1.5">
            <a :href="item.attachment_download_url" target="_blank">
              <VButton size="xs"> Download </VButton>
            </a>
            <a :href="item.attachment_view_url" target="_blank">
              <VButton size="xs"> View </VButton>
            </a>
            <VButton
              v-if="isSiteAdmin"
              size="xs"
              :click="() => deleteDocument(item)"
              :loading="actionsMenu.actions.value.delete?.loading"
            >
              Delete
            </VButton>
          </div>
        </template>
      </VTable>
    </VSection>
  </IndividualLayout>
</template>
